/**
 * D-EZ-110 — FAREWELL TOURS, AND THE CAREER LOOKED BACK ON.
 *
 * THE ENGINE DOES NOT DECIDE A RETIREMENT IN ADVANCE. `aging.ts` rolls it at
 * the winter, on the age he raced the season at and on how the season went
 * (`retirementChance`). So before and during a season the only honest story is
 * speculation — "the last ride?" — from two things that are already true: his
 * age, and the published odds for a man of that age. The one exception is the
 * league's age limit: a driver at `MAX_AGE` is certain to go, and the rule says
 * so before a wheel turns.
 *
 * ⚠ NO LOOK-AHEAD. `farewellWatch(seasons, n)` reads the roster of season n and
 * the history before it — never season n's races, never its winter. Proved in
 * `chapters.lookahead.test.ts` by doctoring both.
 *
 * When he has gone, `careerRetrospective` tells the whole career off the record
 * book: seasons, wins, titles, the first win and the last, the best year, the
 * shops he drove for and the rivalry people will remember.
 */

import { MAX_AGE, ageOf, baseRetirementChance, retirementChance } from '../aging.ts';
import { allRaces, type HistoryRace } from '../history/index.ts';
import { dollars } from './text.ts';
import { isRealDriver } from '../story/hometown.ts';
import { asHistory, chapterContext, seasonsThrough, teamBase, type ChapterContext, type ChapterSeason } from './league.ts';
import { count, ordinal, plural, surname, tell, tellOne, winterQuote, type Said, type Told } from './text.ts';

/** From this age the published retirement odds reach double figures (14%). */
export const FAREWELL_AGE = 38;

export interface FarewellCandidate {
  driverId: string;
  name: string;
  age: number;
  /** The published chance of walking away after this season, by result (0-1). */
  odds: { base: number; champion: number; topTen: number; winner: number; back: number };
  /** True only at the league's age limit — the rule decides it, not a roll. */
  certain: boolean;
  careerWins: number;
  titles: number;
  seasons: number;
  lines: Told[];
  quote: Said | null;
}

const pct = (p: number) => Math.round(p * 100);

/**
 * The veterans whose age puts the published odds of retiring after season
 * `seasonNumber` into double figures — the "last ride?" storyline — biggest
 * careers first. Reads season n's roster and the seasons before it only.
 */
export function farewellWatch(input: readonly ChapterSeason[], seasonNumber: number, opts: { limit?: number; cx?: ChapterContext } = {}): FarewellCandidate[] {
  const before = seasonsThrough(input, seasonNumber - 1, { winter: true });
  const current = input.find((s) => s.seasonNumber === seasonNumber);
  const prev = before[before.length - 1];
  const roster = current?.roster ?? (prev?.seasonNumber === seasonNumber - 1 ? prev.offseason?.roster : undefined);
  if (!roster || roster.length === 0) return [];
  const cx = opts.cx ?? chapterContext(before, input);
  const field = roster.length;
  const out: FarewellCandidate[] = [];
  const used = new Set<string>();
  for (const card of roster) {
    const age = ageOf(card);
    if (age < FAREWELL_AGE) continue;
    const e = cx.registry.byId[card.driverId];
    const careerWins = e?.career.wins ?? 0;
    const titles = e?.career.championships ?? 0;
    const seasons = e?.career.seasons ?? 0;
    // A farewell tour is for a career worth one: a winner, a champion, a long servant — or the age limit.
    if (age < MAX_AGE && careerWins === 0 && titles === 0 && seasons < 4) continue;
    const odds = {
      base: baseRetirementChance(age),
      champion: retirementChance(age, { rank: 1, wins: 1, seasons, unranked: false, fieldSize: field }),
      topTen: retirementChance(age, { rank: 10, wins: 0, seasons, unranked: false, fieldSize: field }),
      winner: retirementChance(age, { rank: 20, wins: 1, seasons, unranked: false, fieldSize: field }),
      back: retirementChance(age, { rank: field, wins: 0, seasons, unranked: false, fieldSize: field }),
    };
    const certain = age >= MAX_AGE;
    const key = `farewell|${cx.leagueKey}|${seasonNumber}|${card.driverId}`;
    const lines: Told[] = [];
    const slots = { name: card.name, age, base: pct(odds.base), champ: pct(odds.champion), top: pct(odds.topTen), back: pct(odds.back), max: MAX_AGE };
    if (certain) {
      lines.push(tell('{name} is {age}, and the league’s licence stops at {max}: this is his last ride, and everybody knows it.', slots));
    } else {
      lines.push(
        tellOne(key, [
          'At {age}, the league’s own table gives {name} a {base}% chance of stepping away after this season — {top}% after a top-ten year, {back}% if it ends at the back.',
          '{name} is {age}. Men his age walk away {base}% of the time; a title would cut that to {champ}%, a season at the back would push it to {back}%.',
          'Is this the last ride for {name}? At {age} the published odds say {base}% — less if he is winning, {back}% if he is not.',
        ], slots),
      );
    }
    if (e && e.career.starts > 0) {
      const tSlots: Record<string, string | number> = { seasons: count(e.career.seasons), s: plural(e.career.seasons, 'season'), wins: e.career.wins, w: plural(e.career.wins, 'win'), starts: e.career.starts };
      if (titles > 0) lines.push(tell('He brings {titles} {t} and {wins} {w} into it, from {starts} starts.', { ...tSlots, titles: count(titles), t: plural(titles, 'title') }));
      else if (e.career.wins > 0) lines.push(tell('He has {wins} {w} from {starts} starts.', tSlots));
      else lines.push(tell('{starts} starts and still looking for the first win.', tSlots));
    }
    out.push({
      driverId: card.driverId,
      name: card.name,
      age,
      odds,
      certain,
      careerWins,
      titles,
      seasons,
      lines,
      quote: winterQuote('last-ride', card.driverId, card.name, cx.people, {}, `${seasonNumber}`, used),
    });
  }
  out.sort((a, b) => Number(b.certain) - Number(a.certain) || b.titles - a.titles || b.careerWins - a.careerWins || b.age - a.age || a.driverId.localeCompare(b.driverId));
  return out.slice(0, opts.limit ?? 6);
}

// ---------------------------------------------------------------------------
// The career, looked back on
// ---------------------------------------------------------------------------

export interface Retrospective {
  driverId: string;
  name: string;
  real: boolean;
  lines: Told[];
  /** The rivals people will remember, hottest peak first. */
  rivals: { otherId: string; otherName: string; peak: number; worst: string | null; ahead: number; behind: number }[];
  titles: number[];
  firstWin: HistoryRace | null;
  lastWin: HistoryRace | null;
}

/**
 * A career told off the record book, through season `through` (its winter
 * included). Null for a driver who never started a race.
 */
export function careerRetrospective(
  input: readonly ChapterSeason[],
  driverId: string,
  through: number,
  cx?: ChapterContext,
  opts: { seasonsSaid?: boolean } = {},
): Retrospective | null {
  const seasons = seasonsThrough(input, through, { winter: true });
  const ctx = cx ?? chapterContext(seasons);
  const e = ctx.registry.byId[driverId];
  if (!e || e.league.starts === 0) return null;
  const real = isRealDriver(driverId);
  const races = allRaces(asHistory(seasons));
  const wins = races.filter((r) => r.winnerId === driverId);
  const firstWin = wins[0] ?? null;
  const lastWin = wins[wins.length - 1] ?? null;
  const titles = e.bySeason.filter((l) => l.champion).map((l) => l.seasonNumber);
  const key = `retro|${ctx.leagueKey}|${driverId}`;
  const lines: Told[] = [];
  const L = e.league;
  const base = { name: e.name, seasons: count(L.seasons), s: plural(L.seasons, 'season'), starts: L.starts, wins: L.wins, w: plural(L.wins, 'win'), top10: L.top10, poles: L.poles };
  lines.push(
    opts.seasonsSaid
      ? L.wins > 0
        ? tellOne(`${key}|sum`, ['In all: {starts} starts, {wins} {w}, {top10} top tens.', 'The career line reads {starts} starts, {wins} {w} and {top10} top tens.', 'He made {starts} starts and won {wins} of them.'], base)
        : tellOne(`${key}|sum`, ['In all: {starts} starts and {top10} top tens, but never a win.', '{starts} starts, and the win never came.'], base)
      : L.wins > 0
        ? tellOne(`${key}|sum`, ['{seasons} {s} in this league: {starts} starts, {wins} {w}, {top10} top tens.', '{name} ran {starts} races over {seasons} {s} here and won {wins} of them.'], base)
        : tellOne(`${key}|sum`, ['{seasons} {s} and {starts} starts in this league, {top10} of them in the top ten — but never a win.', '{name} made {starts} starts over {seasons} {s}. The win never came.'], base),
  );
  if (e.prior && (e.prior.wins ?? 0) > 0) {
    lines.push(tell('Counting the career he brought with him, {cw} wins in all.', { cw: e.career.wins }));
  }
  if (titles.length > 0) {
    lines.push(
      titles.length === 1
        ? tell('Champion in season {t}.', { t: titles[0] })
        : tell('Champion {n} times — seasons {list}.', { n: count(titles.length), list: listOf(titles.map(String)) }),
    );
  }
  if (firstWin && lastWin && firstWin !== lastWin) {
    lines.push(tell('The first win came in the {r1} in season {s1}; the last, the {r2} in season {s2}.', { r1: firstWin.raceName, s1: firstWin.seasonNumber, r2: lastWin.raceName, s2: lastWin.seasonNumber }));
  } else if (firstWin) {
    lines.push(tell('His one win: the {r1}, in season {s1}.', { r1: firstWin.raceName, s1: firstWin.seasonNumber }));
  }
  const best = e.bestSeason;
  if (best && !best.champion) {
    lines.push(
      best.wins > 0
        ? tell('His best year was season {n}: {rank} in the championship, with {w} {wl}.', { n: best.seasonNumber, rank: ordinal(best.rank), w: best.wins, wl: plural(best.wins, 'win') })
        : tell('His best year was season {n}, {rank} in the championship.', { n: best.seasonNumber, rank: ordinal(best.rank) }),
    );
  }
  // The shops, in order, with how long he stayed.
  const stints: { name: string; n: number }[] = [];
  let lastBase: string | null = null;
  for (const l of [...e.bySeason].sort((a, b) => a.seasonNumber - b.seasonNumber)) {
    const b = teamBase(l.teamId);
    const name = l.teamName ?? 'his own car';
    if (b === lastBase && stints.length > 0) {
      stints[stints.length - 1].n += 1;
      stints[stints.length - 1].name = name;
    } else stints.push({ name, n: 1 });
    lastBase = b;
  }
  if (stints.length === 1) lines.push(stints[0].name === 'his own car' ? tell('He ran his own car the whole way.') : tell('One shop the whole way: {shop}.', { shop: stints[0].name }));
  else if (stints.length > 1) lines.push(tell('His rides, in order: {shops}.', { shops: stints.map((x) => (x.n > 1 ? `${x.name} (${x.n} seasons)` : x.name)).join(', then ') }));
  if (L.winningsMb > 0) lines.push(tell('{money} won on the race track.', { money: dollars(L.winningsMb) }));
  // The rivalries, by how hot they ever got.
  const rivals = [...ctx.people.book.byPair.values()]
    .filter((r) => (r.a === driverId || r.b === driverId) && r.peak >= 11)
    .sort((a, b) => b.peak - a.peak || a.a.localeCompare(b.a))
    .slice(0, 2)
    .map((r) => {
      const mine = r.a === driverId;
      return {
        otherId: mine ? r.b : r.a,
        otherName: mine ? r.bName : r.aName,
        peak: r.peak,
        worst: r.worst?.text ?? null,
        ahead: mine ? r.headToHead.aAhead : r.headToHead.bAhead,
        behind: mine ? r.headToHead.bAhead : r.headToHead.aAhead,
      };
    });
  const top = rivals[0];
  if (top) {
    lines.push(
      top.worst
        ? tell('The rivalry they will remember is {other}. The worst of it: {worst}', { other: top.otherName, worst: top.worst })
        : tell('The rivalry they will remember is {other}.', { other: top.otherName }),
    );
    if (top.ahead + top.behind > 0) lines.push(tell('He finished ahead of {other} {a} times and behind him {b}.', { other: surname(top.otherName), a: top.ahead, b: top.behind }));
  }
  return { driverId, name: e.name, real, lines, rivals, titles, firstWin, lastWin };
}

export function listOf(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
